/* src/components/Login.js - v3.5 - Store The Server Login Token
   CHANGELOG (v3.4 -> v3.5): POST /api/login now returns session_token / session_expires
   (routes/auth.py v2.9). The token is moved out of the user object into its own localStorage key
   ('nsn_session_token', read by the fetch wrapper in index.js v1.1) so it is not saved inside
   'nsn_user' and not passed around the UI with the profile data.
   CHANGELOG (v3.3 -> v3.4): Added a "Get it from Microsoft Store" badge
   in the same spot as the Google Play badge below, shown only when
   !isMobileViewport (desktop) - the Google Play badge is untouched and
   stays mobile-only. Uses Microsoft's own official badge SVG
   (ms-store-badge.svg, sourced from get.microsoft.com) the same way the
   Google Play badge uses Google's official PNG - not a custom icon.
   CHANGELOG (v3.2 -> v3.3): The deactivated-account login error now
   points to a real KB article (routes/auth.py's login() added
   kb_link/kb_link_label to that 403 response now that kb.nubix.ai's
   'Account Deactivated? How to Reinstate' article exists), but the
   error div just renders {error} as plain text - a raw URL in there
   would show as inert, uncopyable-looking text, not a link. Added a
   separate errorLink state so the response's link renders as an
   actual <a> below the message instead.
   CHANGELOG (v3.1 -> v3.2): Added a "Get it on Google Play" badge near the
   bottom of the login card (visible in both web and mobile layouts - see
   App.css v2.17's .playstore-badge-img for the responsive sizing). On
   Android specifically, the badge links through an intent: URL
   (scheme nubixsocial, package com.nubix.social) instead of the plain
   Play Store URL - this is the standard library-free way to ask the OS to
   open the app if it's installed, with S.browser_fallback_url handling
   the "not installed" case by falling straight back to the Play Store
   listing. Desktop/non-Android just gets the plain Play Store link. */
import React, { useState, useEffect } from 'react';

const PLAYSTORE_URL = 'https://play.google.com/store/apps/details?id=com.nubix.social';
const MSSTORE_URL = 'https://apps.microsoft.com/detail/9p2g78t1gdrs?hl=en-US&gl=US';

function Login({ onLogin, onClose }) {
  const [isRegistering, setIsRegistering] = useState(false);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [errorLink, setErrorLink] = useState(null);

  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotUsername, setForgotUsername] = useState('');
  const [forgotStatus, setForgotStatus] = useState('');

  const isAndroid = /Android/i.test(navigator.userAgent);
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== 'undefined' && window.innerWidth <= 768
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const handleChange = (e) => setIsMobileViewport(e.matches);
    mq.addEventListener('change', handleChange);
    return () => mq.removeEventListener('change', handleChange);
  }, []);
  const playStoreHref = isAndroid
    ? `intent://#Intent;scheme=nubixsocial;package=com.nubix.social;S.browser_fallback_url=${encodeURIComponent(PLAYSTORE_URL)};end`
    : PLAYSTORE_URL;

  // ESC Key Listener
  useEffect(() => {
      const handleEsc = (e) => {
          if (e.key === 'Escape' && onClose) {
              onClose();
          }
      };
      window.addEventListener('keydown', handleEsc);
      return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setErrorLink(null);

    if (isRegistering && !email) {
        setError("Email is required for registration.");
        return;
    }

    const endpoint = isRegistering ? '/api/register' : '/api/login';
    const payload = { username, password };
    if (isRegistering) payload.email = email;
    
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      const data = await res.json();
      
      if (res.ok) {
        if (isRegistering) {
            // Flag read once by App.js's handleLogin() right after this
            // same person logs in - opens the composer pre-filled with an
            // intro-post prompt instead of dropping them into an empty
            // feed. sessionStorage (not state) since registration and the
            // login that follows it are two separate form submissions.
            sessionStorage.setItem('nsn_just_registered', '1');
            alert("Account created! Please login.");
            setIsRegistering(false);
            setPassword('');
        } else {
            if (data.session_token) {
                try { localStorage.setItem('nsn_session_token', data.session_token); } catch (e) { /* storage unavailable: the user just stays on the old, token-less path */ }
            }
            delete data.session_token;
            delete data.session_expires;
            onLogin(data);
        }
      } else {
        setError(data.error || 'Something went wrong');
        if (data.kb_link) setErrorLink({ url: data.kb_link, label: data.kb_link_label || 'Learn more' });
      }
    } catch (err) {
      setError('Server error. Please try again later.');
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setForgotStatus('Sending instructions...');
    try {
      const res = await fetch('/api/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: forgotUsername })
      });
      const data = await res.json();
      if (res.ok) {
        setForgotStatus('Email sent! Check your inbox.');
        setForgotUsername('');
      } else {
        setForgotStatus(data.error || 'User not found.');
      }
    } catch (err) {
      setForgotStatus('Server error.');
    }
  };

  return (
    <div 
        style={{ 
            position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', 
            display: 'flex', alignItems: 'center', justifyContent: 'center', 
            zIndex: 10000, background: 'rgba(0, 0, 0, 0.85)', backdropFilter: 'blur(8px)'
        }} 
        onClick={onClose} 
    >
      
      <div 
        className="card" 
        style={{ width: '90%', maxWidth: '400px', padding: '40px', textAlign: 'center', position: 'relative' }}
        onClick={(e) => e.stopPropagation()} 
      >
        <button 
            onClick={onClose}
            style={{ 
                position: 'absolute', top: '15px', right: '15px', background: 'none', border: 'none', 
                color: '#fff', fontSize: '24px', cursor: 'pointer', zIndex: 20, opacity: 0.7 
            }}
            title="Close"
        >
            ✕
        </button>

        {/* UPDATED TITLE */}
        <h2 style={{ marginBottom: '10px', color: 'white', textTransform: 'uppercase', letterSpacing: '2px', fontSize: '24px' }}>
          NUBIX SOCIAL NETWORK
        </h2>
        
        <p style={{ color: '#a0a0b0', marginBottom: '30px', fontSize: '0.9rem' }}>
            {isRegistering ? 'Create an account to join the conversation.' : 'Login to access your profile and messages.'}
        </p>
        
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <input 
            type="text" 
            placeholder="Username" 
            value={username} 
            onChange={(e) => setUsername(e.target.value)}
            className="post-input"
            autoFocus
            required
          />
          
          {isRegistering && (
            <input 
                type="email" 
                placeholder="Email Address" 
                value={email} 
                onChange={(e) => setEmail(e.target.value)}
                className="post-input"
                required
            />
          )}

          <input 
            type="password" 
            placeholder="Password" 
            value={password} 
            onChange={(e) => setPassword(e.target.value)}
            className="post-input"
            required
          />
          
          {error && (
              <div style={{ color: '#ff4757', fontSize: '14px', background: 'rgba(255, 71, 87, 0.1)', padding: '10px', borderRadius: '8px' }}>
                  {error}
                  {errorLink && (
                      <div style={{ marginTop: '8px' }}>
                          <a href={errorLink.url} target="_blank" rel="noopener noreferrer" style={{ color: '#ff6b81', fontWeight: 'bold', textDecoration: 'underline' }}>
                              {errorLink.label}
                          </a>
                      </div>
                  )}
              </div>
          )}
          
          <button 
            type="submit" 
            style={{ 
                padding: '12px', borderRadius: '30px', border: 'none', 
                background: 'linear-gradient(45deg, #ff4757, #ff6b81)', 
                color: 'white', fontWeight: 'bold', cursor: 'pointer', fontSize: '16px' 
            }}
          >
            {isRegistering ? 'Create Account' : 'Login'}
          </button>
        </form>

        <div style={{ marginTop: '20px', fontSize: '14px', color: '#a0a0b0' }}>
          {isRegistering ? 'Already have an account? ' : "Don't have an account? "}
          <span 
            onClick={() => { setIsRegistering(!isRegistering); setError(''); }} 
            style={{ color: '#ff4757', cursor: 'pointer', fontWeight: 'bold' }}
          >
            {isRegistering ? 'Login' : 'Register'}
          </span>
        </div>

        {!isRegistering && (
             <div style={{ marginTop: '15px', fontSize: '13px' }}>
                 <span
                    onClick={() => { setShowForgotModal(true); setForgotStatus(''); }}
                    style={{ color: '#a0a0b0', cursor: 'pointer', textDecoration: 'underline' }}
                 >
                    Forgot Password?
                 </span>
             </div>
        )}

        {isMobileViewport && (
        <div style={{ marginTop: '25px', paddingTop: '20px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <p style={{ color: '#a0a0b0', fontSize: '12px', marginBottom: '8px' }}>Also available on Android</p>
            <a href={playStoreHref} target={isAndroid ? undefined : '_blank'} rel="noopener noreferrer">
                <img
                    src="/google-play-badge.png"
                    alt="Get it on Google Play"
                    className="playstore-badge-img"
                />
            </a>
        </div>
        )}

        {!isMobileViewport && (
        <div style={{ marginTop: '25px', paddingTop: '20px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <p style={{ color: '#a0a0b0', fontSize: '12px', marginBottom: '8px' }}>Also available on Windows</p>
            <a href={MSSTORE_URL} target="_blank" rel="noopener noreferrer">
                <img
                    src="/ms-store-badge.svg"
                    alt="Get it from Microsoft Store"
                    className="playstore-badge-img"
                />
            </a>
        </div>
        )}
      </div>

      {showForgotModal && (
        <div style={{
            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
            background: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001
        }} onClick={() => setShowForgotModal(false)}>
            <div className="card" style={{ width: '90%', maxWidth: '350px', padding: '30px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                <h3 style={{ color: 'white', marginBottom: '15px' }}>Reset Password</h3>
                <input 
                    type="text" 
                    placeholder="Enter Username" 
                    value={forgotUsername} 
                    onChange={(e) => setForgotUsername(e.target.value)}
                    className="post-input"
                    style={{ marginBottom: '15px' }}
                />
                <button 
                    onClick={handleForgotSubmit}
                    style={{ 
                        width: '100%', padding: '10px', borderRadius: '30px', border: 'none', 
                        background: 'linear-gradient(45deg, #ff4757, #ff6b81)', 
                        color: 'white', fontWeight: 'bold', cursor: 'pointer'
                    }}
                >
                    Send Instructions
                </button>
                {forgotStatus && <div style={{ marginTop: '15px', color: '#ccc', fontSize: '13px' }}>{forgotStatus}</div>}
            </div>
        </div>
      )}
    </div>
  );
}

export default Login;
